/* NPM */
import classNames from 'classnames';
import React from 'react';
/* ICONS*/
import ChevronDownIcon from '@mdi/svg/svg/chevron-down.svg?react';
import CloudCheck from '@mdi/svg/svg/cloud-check.svg?react';
import CloudSync from '@mdi/svg/svg/cloud-sync.svg?react';
import CloudUpload from '@mdi/svg/svg/cloud-upload.svg?react';
import DownloadIcon from '@mdi/svg/svg/download.svg?react';
import NoVideo from '@mdi/svg/svg/movie-remove.svg?react';
/* COMPONENTS */
import { Button, ButtonMenu, Flex, MDIIcon, toast } from '@solinkcorp/solink-components';
/* MISC */
import AnalyticsLogger from 'models/AnalyticsLogger';
/* CONSTANTS */
import {
  CLOUD_STATUS_COMPLETE,
  CLOUD_STATUS_ERROR,
  CLOUD_STATUS_IN_PROGRESS,
  CONTEXT_SAVETOCLOUD,
  type EventContextType,
  MDI_VIEW_BOX,
  type PushToCloudStatus,
} from 'vd-constants';
/* TYPES */
import { type LocaleDefinition } from '@solinkcorp/locale';
import { type CameraVideoSource, type SavePanelCamera } from '../../types';
/* UTILS */
import { type DeviceId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { isKiosk } from 'src/core/utils/kioskUtils';
import { areStreamsBitrateValid } from 'utils/CameraUtil';
import { type GetPushToCloudStatusForOneCam } from '../../SavePanel';
import useCloudDownload from './useCloudDownload';
import usePushToCloudStatus from './usePushToCloudStatus';

const BUTTON_STYLE = { width: '100%', borderRadius: 0 };
const BUTTON_TEXT_STYLE = { fontSize: 12, width: '100%' };

/** A function to transform capital case to dash case except for the first letter.
 * eg. icon component name like 'SvgCloudCheck' to class name 'svg-cloud-check'
 */
function capToDash(str: string) {
  return str
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase()
    .replace(/\s/g, '-');
}

const initiateCloudDownload = (download: any, zipDownload: boolean) => {
  let url = download?.mp4Url;
  if (zipDownload) {
    url = download?.zipUrl;
  }
  window.location.href = url;
  AnalyticsLogger.downloadClip({ download });
};

interface CameraAddonsProps {
  camera: SavePanelCamera;
  canDownload: boolean;
  clipVideoCamera: CameraVideoSource;
  cloudStatus?: PushToCloudStatus;
  contextMode: EventContextType;
  eventId: string | undefined;
  eventTitle: string | undefined;
  isLoading: boolean;
  locale: LocaleDefinition;
  saveInProgress: boolean;
  saveMode: boolean;
  pushVideo: (camera: CameraVideoSource) => Promise<unknown>;
  getPushToCloudStatus: GetPushToCloudStatusForOneCam;
  startTime: Date | undefined;
  endTime: Date | undefined;
}

const CameraAddons: React.FC<CameraAddonsProps> = (props) => {
  const {
    camera,
    canDownload,
    clipVideoCamera,
    cloudStatus,
    contextMode,
    eventId,
    eventTitle,
    getPushToCloudStatus,
    isLoading,
    locale,
    pushVideo,
    saveInProgress,
    saveMode,
    startTime,
    endTime,
  } = props;
  const deviceId = camera.deviceId as DeviceId | undefined;
  const { streamLabel, sourceLabel, source, streamId, streamName } = clipVideoCamera;

  const noVideo = !source;
  const clipError = cloudStatus === CLOUD_STATUS_ERROR;
  const savedStream = camera.streams.find((stream) => stream.totalSegments != null);
  const allowDownload = !isKiosk() && canDownload;

  const { customerId: tenantId } = useUserSession();

  /** When we're processing any user request */
  const [isProcessing, setIsProcessing] = React.useState(false);
  /** If we should keep pulling for the status of video pushing to cloud or the downloadable file's availability */
  const [shouldRefreshPushToCloudStatus, setShouldRefreshPushToCloudStatus] = React.useState(false);
  const [shouldRefreshCloudDownload, setShouldRefreshCloudDownload] = React.useState(false);

  // Status of the downloadable file's availability
  const { cloudDownload } = useCloudDownload({
    eventId,
    cameraId: camera.id,
    cameraName: camera.name,
    streamId,
    tenantId,
    shouldRefreshCloudDownload,
  });

  const downloadLinkReady = !!cloudDownload && cloudDownload.state === CLOUD_STATUS_COMPLETE;
  const downloadLinkError = !!cloudDownload && cloudDownload.state === CLOUD_STATUS_ERROR;
  const saveCompleted =
    !!eventId &&
    contextMode === CONTEXT_SAVETOCLOUD &&
    !saveInProgress &&
    !clipError &&
    !downloadLinkError &&
    !isLoading;
  const saveSucceeded = saveCompleted && cloudStatus === CLOUD_STATUS_COMPLETE;
  const isDownloadReady = saveCompleted && allowDownload && !noVideo && downloadLinkReady;
  const areNoVideo = saveCompleted && noVideo;

  /**
   * Get the status of the video pushing to Cloud
   * It should only fire when the cilp save is completed but the downloadable file is not ready.
   */
  const shouldCheckPushToCloudStatus = saveSucceeded && !noVideo && !downloadLinkReady;
  const { pushToCloudStatus, mutatePushToCloudStatus } = usePushToCloudStatus({
    deviceId,
    cameraId: camera.id,
    streamId,
    shouldCheckPushToCloudStatus,
    shouldRefreshPushToCloudStatus,
    getPushToCloudStatus,
  });

  const isPushingToCloud = pushToCloudStatus === 'inProgress';
  const isPushToCloudComplete = pushToCloudStatus === 'complete' || pushToCloudStatus === 'verify';
  const isPushToCloudError = pushToCloudStatus === 'error';

  const isPreparingDownload =
    saveCompleted && allowDownload && !noVideo && !downloadLinkReady && isPushToCloudComplete;

  const videoDidNotUpload =
    (savedStream?.totalSegments === 0 || savedStream?.totalSegments === -1) && isPushToCloudError;
  // Render Retry button if the download link is not ready
  // and stream of the camera has totalSegments -1.
  // -1 means the video of the stream probably never get pushed to the cloud.
  // Or if the video is in cloud, it's never been associated with the clip
  // so the event doesn't have the totalSegments data.
  const videoAvailableButNotInClip =
    saveCompleted && allowDownload && !noVideo && !downloadLinkReady && videoDidNotUpload;
  // The clip is marked as completed when any camera from the clip finished upload video to cloud.
  // So other cameras from the same clip can still be pushing video to cloud.
  const mayStillPushVideo =
    !pushToCloudStatus && saveSucceeded && !areNoVideo && !videoDidNotUpload && !downloadLinkReady;

  const hasDownloadOptions = !!isDownloadReady && !!cloudDownload?.zipUrl;
  const streamTagClass = classNames('stream-tag', {
    hd: streamLabel?.toLowerCase().includes('hd') || streamLabel?.toLowerCase().includes('cloud'),
  });
  const videoTagClass = classNames('video-tag', { 'no-video': noVideo });

  const retryPushToCloud = React.useCallback(() => {
    if (savedStream?.id) {
      setIsProcessing(true);

      const analyticsParams = {
        eventId,
        eventTitle,
        cameraId: camera.id,
        cameraName: camera.name,
        streamId,
        streamName,
        videoSource: source,
        startTime: startTime?.toISOString(),
        endTime: endTime?.toISOString(),
      };
      AnalyticsLogger.retryClipVideoClick({ ...analyticsParams });

      pushVideo(clipVideoCamera)
        .then(() => {
          setIsProcessing(false);
          mutatePushToCloudStatus(CLOUD_STATUS_IN_PROGRESS);
          toast.success(locale.cloudShare.videoStartedPushing);
          AnalyticsLogger.uploadClipVideo({ ...analyticsParams, isSuccess: true });
        })
        .catch((error) => {
          setIsProcessing(false);
          toast.error(`${locale.error.unableToPushVideoForCamera} ${error}`);
          AnalyticsLogger.uploadClipVideo({
            ...analyticsParams,
            isSuccess: false,
            errorMessage: error,
          });
        });
    } else {
      toast.error(locale.error.unableToPushVideoForCameraMissingStreamId);
    }
  }, [
    savedStream?.id,
    eventId,
    eventTitle,
    camera.id,
    camera.name,
    streamId,
    streamName,
    source,
    startTime,
    endTime,
    pushVideo,
    mutatePushToCloudStatus,
    clipVideoCamera,
    locale,
  ]);

  // Pull the status of video pushing to cloud when the video is still pushing to cloud
  React.useEffect(() => {
    if (isPushingToCloud || mayStillPushVideo) {
      setShouldRefreshPushToCloudStatus(true);
    } else {
      setShouldRefreshPushToCloudStatus(false);
    }
  }, [isPushingToCloud, mayStillPushVideo]);

  // Pull the status of the downloadable file's availability when it's being prepared
  React.useEffect(() => {
    if (isPreparingDownload) {
      setShouldRefreshCloudDownload(true);
    } else {
      setShouldRefreshCloudDownload(false);
    }
  }, [isPreparingDownload]);

  const menuItems = [
    {
      onClick: () => initiateCloudDownload(cloudDownload, true),
      text: locale.download.downloadZip,
      type: 'action',
    },
    {
      onClick: () => initiateCloudDownload(cloudDownload, false),
      text: locale.download.downloadMp4,
      type: 'action',
    },
  ];

  let videoSourceIcon = CloudUpload;
  if (saveInProgress || isProcessing || isPushingToCloud || (saveSucceeded && !isDownloadReady)) {
    videoSourceIcon = CloudSync;
  } else if (noVideo) {
    videoSourceIcon = NoVideo;
  } else if ((source === 'cloud' && !videoAvailableButNotInClip) || downloadLinkReady) {
    videoSourceIcon = CloudCheck;
  }

  const videoSourceIconClass = classNames({
    [`${capToDash(videoSourceIcon.name)}`]: videoSourceIcon.name,
  });
  const downloadButtonClass = classNames('download-button', {
    'has-download-options': hasDownloadOptions,
  });
  const shouldRenderCameraOfflineTag = !areStreamsBitrateValid(camera.streams);
  const shouldRenderSource = saveMode || noVideo;
  const shouldRenderStreamLabel = streamLabel && !downloadLinkReady;

  const buttonProps = React.useMemo(() => {
    if (areNoVideo) {
      return {
        disabled: true,
        text: locale.cloudShare.noVideo,
      };
    }
    if (isDownloadReady) {
      return {
        icon: <DownloadIcon className="icon" viewBox={MDI_VIEW_BOX} height={18} width={18} />,
        onClick: () => initiateCloudDownload(cloudDownload, hasDownloadOptions),
        text: locale.download.download,
        dataTestId: 'clip-download-button',
      };
    }
    if (isPreparingDownload) {
      return {
        disabled: true,
        text: locale.download.statusPreparing,
      };
    }
    if (isPushingToCloud) {
      return {
        disabled: true,
        text: locale.pushToCloudState.uploading,
      };
    }
    if (videoAvailableButNotInClip) {
      if (isProcessing) {
        return {
          disabled: true,
          text: locale.cloudShare.processing,
        };
      }
      return {
        colorMode: 'light-gray' as const,
        icon: <MDIIcon icon={CloudUpload} size={20} />,
        text: locale.general.retry,
        onClick: retryPushToCloud,
      };
    }
  }, [
    areNoVideo,
    isDownloadReady,
    isPreparingDownload,
    isPushingToCloud,
    videoAvailableButNotInClip,
    isProcessing,
    locale,
    cloudDownload,
    hasDownloadOptions,
    retryPushToCloud,
  ]);

  return (
    <>
      <Flex className="tags-wrapper">
        {shouldRenderStreamLabel && <div className={streamTagClass}>{streamLabel}</div>}
        {shouldRenderSource && <div className={videoTagClass}>{sourceLabel}</div>}
        {shouldRenderCameraOfflineTag && (
          <div className="camera-tag"> {locale.camera.offline} </div>
        )}
      </Flex>
      <Flex className="source-icon-wrapper" justifyContent="center" alignItems="center">
        <MDIIcon className={videoSourceIconClass} icon={videoSourceIcon} size={24} />
      </Flex>
      {!!buttonProps && (
        <Flex alignItems="stretch">
          <Button
            className={downloadButtonClass}
            style={BUTTON_STYLE}
            textStyle={BUTTON_TEXT_STYLE}
            {...buttonProps}
          />
          {hasDownloadOptions && (
            <div>
              <ButtonMenu
                buttonIcon={
                  <ChevronDownIcon className="icon" viewBox={MDI_VIEW_BOX} height={24} width={24} />
                }
                className="button-menu-download-options"
                buttonIconOnly
                buttonType="clear"
                colorMode="light"
                menuItems={menuItems}
                tooltipVerticalOffset={-5}
              />
            </div>
          )}
        </Flex>
      )}
    </>
  );
};

export default React.memo(CameraAddons);
